import { ChangeDetectorRef, Component, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { DepartmentItem, Summary } from 'components/queue-view/live-workload-tab/models/department-item';
import { DepartmentNameColumn, ServiceColumn, ServiceColumns } from 'components/queue-view/live-workload-tab/models/column-config';
import { ServiceAvailabilityCollectorService } from 'shared/services/service-availability-collector/service-availability-collector-service';
import { WorkItemTypes } from 'common/workitems/constants/workitem-types';
import { DepartmentDropdownItem } from 'components/queue-view/models/dropdown-item.interface';
import { Subscription } from 'rxjs';
import { PollingService } from 'shared/services/polling-service/polling.service';
import { SortDirection } from 'components/queue-view/live-workload-tab/models/sort';
import { BcRestApi } from 'common/api/rest-api';
import { AssignedCount, DepartmentLoadsModel, StatusCount } from 'common/api/models/department-loads.model';
import { StatusTypes } from 'common/common/constants/status-types';
import { LiveWorkloadTabHelper } from 'components/queue-view/live-workload-tab/live-workload-tab.helper';
import { BackendFieldConverterService } from 'shared/services/backend-field-converter/backend-field-converter.service';
import { TabSettings } from 'components/queue-view/live-workload-tab/interfaces/tab-settings';
import { IntersectionStatus } from 'shared/directives/visibility-observer/from-intersection-observer';
import { isEmpty } from 'lodash';
import { DepartmentListTabService } from 'components/queue-view/live-workload-tab/department-list-tab/department-list-tab.service';
import { ServiceTypes } from 'common/common/constants/service-types';
import { MaxLongestWaitingTimes } from 'common/api/models/max-longest-waiting-times.model';
import { BcFeatures } from 'common/common/constants/feature-enums';
import { BcSettingsService } from 'common/settings/settings.service';

@Component({
  selector: 'app-department-list-tab',
  templateUrl: './department-list-tab.component.html',
  styleUrls: ['./department-list-tab.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class DepartmentListTabComponent implements OnChanges, OnDestroy {
  @Input()
  selectedDepartments: DepartmentDropdownItem[] = [];

  @Output()
  navigateToAgentsTab = new EventEmitter<string[]>();

  @Output()
  navigateToOtherTab = new EventEmitter<TabSettings>();

  @ViewChild('virtualDepartmentList')
  virtualDepartmentList: CdkVirtualScrollViewport;

  public static readonly REFRESH_INTERVAL = 5 * 60 * 1000; // ms

  public departmentList: DepartmentItem[] = [];
  public displayedDepartmentList: DepartmentItem[] = [];
  public serviceColumns: ServiceColumn[] = [];
  public services: WorkItemTypes[] = [];
  public activeSort: SortDirection = SortDirection.ASC;
  public sortDirection = SortDirection;
  public hasScrollSeparator: boolean = false;
  public statusTypes = StatusTypes;
  public summary: Summary = {};
  public omniIncludedServices: ServiceColumn[] = [];
  public servicesWithBotIncludedInOmni: ServiceColumn[] = [];

  private _departmentsSubscription: Subscription;
  private _maxLongestWaitingTimes: MaxLongestWaitingTimes = {};
  private _hasNanorepIntegration: boolean = false;

  constructor(private _serviceCollectorService: ServiceAvailabilityCollectorService,
              private _pollingService: PollingService,
              private _restApi: BcRestApi,
              private _backendFieldConverterService: BackendFieldConverterService,
              private _changeDetector: ChangeDetectorRef,
              private _departmentListTabService: DepartmentListTabService,
              private _settingsService: BcSettingsService) {
    this._serviceCollectorService.getAvailableServicesByWorkItemTypeValue().then((services) => {
      this.services = services;
      this._serviceCollectorService.servicesIncludedInOmni()
        .map(serviceType => this._backendFieldConverterService.serviceTypeConverter(serviceType.toString()))
        .map(workItemType => ServiceColumns.find(item => item.workItemType === workItemType))
        .forEach(item => this.omniIncludedServices.push(item));

      this._settingsService.getSettings().then(settings => {
        this._hasNanorepIntegration = this._settingsService.ChatFeatures.isEnabled(settings, BcFeatures.LC_INTEGRATIONS_NANOREP);
        this.servicesWithBotIncludedInOmni = this.omniIncludedServices.filter(service => this.hasBot(service));
      });

      this.serviceColumns = ServiceColumns.filter(serviceHeader => this.services.includes(serviceHeader.workItemType));
    });
  }

  @HostListener('window:resize', ['$event'])
  public onResize(): void {
    if (this.virtualDepartmentList) {
      this.virtualDepartmentList.checkViewportSize();
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedDepartments) {
      if (changes.selectedDepartments.currentValue !== changes.selectedDepartments.previousValue) {
        this._initSubscriptions();
      }
    }
  }

  public ngOnDestroy(): void {
    if (this._departmentsSubscription) {
      this._departmentsSubscription.unsubscribe();
    }
  }

  private _initSubscriptions(): void {
    this.summary = {};
    if (this._departmentsSubscription) {
      this._departmentsSubscription.unsubscribe();
    }

    this.departmentList = this.selectedDepartments.map(department => ({ name: department.name, id: department.id }));
    this._sortData();
    this._updateList();

    const departmentIds = this.departmentList.map(department => department.id);
    const allServiceType = [...this.serviceColumns, ...this.omniIncludedServices];
    const serviceTypes = allServiceType.map(column => column.serviceType);

    if (departmentIds.length > 0) {
      this._departmentsSubscription = this._pollingService.poll(
        () => this._restApi.getDepartmentLoads(departmentIds, serviceTypes),
        DepartmentListTabComponent.REFRESH_INTERVAL
      ).subscribe((departmentLoadsModel) => {
        this._departmentDataUpdated(departmentLoadsModel);
      });
    }
  }

  public hasDepartments(): boolean {
    return this.displayedDepartmentList.length > 0;
  }

  public hasMoreThanOneDepartment(): boolean {
    return this.displayedDepartmentList.length > 1;
  }

  public getIconByService(workItemType: WorkItemTypes): string {
    return LiveWorkloadTabHelper.getIconByWorkItemType(workItemType);
  }

  public isOmniService(serviceType): boolean {
    return serviceType === ServiceTypes.OMNI;
  }

  public getTooltip(workItemType: WorkItemTypes): string {
    return LiveWorkloadTabHelper.getTooltip('departmentWorkload', workItemType);
  }

  public hasBot(service: ServiceColumn): boolean {
    if (this._hasNanorepIntegration) {
      if (this.isOmniService(service.serviceType)) {
        return this.omniIncludedServices.some(service => (service.workItemType === WorkItemTypes.CHAT) || (service.workItemType === WorkItemTypes.ASYNC_MESSAGE));
      }
      return [WorkItemTypes.CHAT, WorkItemTypes.ASYNC_MESSAGE].includes(service.workItemType);
    }
    return false;
  }

  public toggleSorting(): void {
    this.activeSort = this.isAsc ? SortDirection.DESC : SortDirection.ASC;
    this._sortData();
    this._updateList();
  }

  public get isAsc(): boolean {
    return this.activeSort === SortDirection.ASC;
  }

  public get departmentNameColumnWidth(): string {
    return DepartmentNameColumn.baseWidth;
  }

  public get inverseOfTranslation(): string {
    const viewport = <any>this.virtualDepartmentList;

    if (!viewport || !viewport._renderedContentOffset) {
      return '-0px';
    }

    let offset = viewport._renderedContentOffset;
    return `-${offset}px`;
  }

  public get scrollHelperTransform(): string {
    return `translateX(-${this.departmentNameColumnWidth})`;
  }

  public get headerStickyOffset(): string {
    return `calc(${this.departmentNameColumnWidth} + 20px)`;
  }

  public getStatusCount(department: DepartmentItem, service: ServiceColumn): StatusCount | null {
    if (!department.statusCounts) {
      return null;
    }
    return department.statusCounts[service.serviceType] || { [StatusTypes.AVAILABLE]: 0, [StatusTypes.AWAY]: 0 };
  }

  public getTotalStatusCount(service: ServiceColumn): StatusCount | null {
    if (isEmpty(this.summary.statusCounts)) {
      return null;
    }
    return this.summary.statusCounts[service.serviceType] || { [StatusTypes.AVAILABLE]: 0, [StatusTypes.AWAY]: 0 };
  }

  public getAssignedCount(department: DepartmentItem, service: ServiceColumn): AssignedCount | null {
    if (!department.assignedCounts) {
      return null;
    }

    return department.assignedCounts[service.serviceType] || null;
  }

  public getTotalAssignedCount(service: ServiceColumn): AssignedCount | null {
    if (!this.summary.assignedCounts) {
      return null;
    }

    return this.summary.assignedCounts[service.serviceType] || null;
  }

  public getQueueCount(department: DepartmentItem, service: ServiceColumn): number | null {
    if (!department.queueCounts) {
      return null;
    }

    return department.queueCounts[service.serviceType] || 0;
  }

  public getTotalQueueCount(service: ServiceColumn): number | null {
    if (!this.summary.queueCounts) {
      return null;
    }

    return this.summary.queueCounts[service.serviceType] || 0;
  }

  public getLongestWaiting(department: DepartmentItem, service: ServiceColumn): number | null {
    if (!department.longestWaitingTimes) {
      return null;
    }

    return department.longestWaitingTimes[service.serviceType];
  }

  public getLongestWaitingIconForOmni(department: DepartmentItem): string {
    const longestWaitingService = Object.keys(department.longestWaitingTimes)
      .filter(key => {
        return key !== ServiceTypes.OMNI && department.longestWaitingTimes[key] === department.longestWaitingTimes[ServiceTypes.OMNI];
      });

    const workItemType = this._backendFieldConverterService.serviceTypeConverter(longestWaitingService[0]);
    return this.getIconByService(workItemType);
  }

  public isMaxLongestWaitingTime(department: DepartmentItem, service: ServiceColumn): boolean {
    return this._maxLongestWaitingTimes[service.serviceType].id === department.id;
  }

  public getMaxLongestWaitingByService(service): string {
    return this._maxLongestWaitingTimes[service.serviceType] ? this._maxLongestWaitingTimes[service.serviceType].name : null;
  }

  public onStatusDrilldown(service: ServiceColumn, department?: DepartmentItem): void {
    let status;
    let departments;
    if (department) {
      status = this.getStatusCount(department, service);
      departments = [department.id];
    } else {
      departments = this.selectedDepartments.map(department => department.id);
      status = this.getTotalStatusCount(service);
    }
    if (this.isClickableStatus(status)) {
      this.navigateToAgentsTab.emit(departments);
    }
  }

  public onQueueDrilldown(service: ServiceColumn, department?: DepartmentItem): void {
    let queueCount;
    if (department) {
      queueCount = this.getQueueCount(department, service);
      this._navigateToQueueTab(queueCount, service, [department.id]);
    } else {
      queueCount = this.summary.queueCounts[service.serviceType];
      const departmentIds = this.selectedDepartments.map(department => department.id);
      this._navigateToQueueTab(queueCount, service, departmentIds);
    }
  }

  public onAssignedDrilldown(service: ServiceColumn, department?: DepartmentItem): void {
    if (department) {
      const assignedCount = this.getAssignedCount(department, service);
      this._navigateToAssignedTab(assignedCount, service, [department.id]);
    } else {
      const departmentIds = this.selectedDepartments.map(department => department.id);
      this._navigateToAssignedTab(this.summary.assignedCounts[service.serviceType], service, departmentIds);
    }
  }

  public getAgentCountsText(signedInAgentsCount: number): string {
    return signedInAgentsCount >= 1 ? 'departmentListTab.numberOfAgentsInDepartment' : 'departmentListTab.numberOfAgentsInDepartmentSingular';
  }

  public onVisibilityChanged(status: IntersectionStatus): void {
    this.hasScrollSeparator = status === IntersectionStatus.NotVisible;
  }

  public isClickableStatus(statusCount: StatusCount | null): boolean {
    if (!statusCount) {
      return false;
    }

    return (statusCount[StatusTypes.AWAY] || 0) !== 0 || (statusCount[StatusTypes.AVAILABLE] || 0) !== 0;
  }

  public isClickableCount(count: number): boolean {
    return (count || 0) !== 0;
  }

  private _updateList(): void {
    this.displayedDepartmentList = [...this.departmentList];
    this._changeDetector.detectChanges();
  }

  private _sortData(): void {
    if (this.activeSort === SortDirection.ASC) {
      this.departmentList.sort((a, b) => this._isNoDepartment(a) ? -1 : this._isNoDepartment(b) ? 1 : a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
    } else {
      this.departmentList.sort((a, b) => this._isNoDepartment(a) ? -1 : this._isNoDepartment(b) ? 1 : b.name.toLowerCase().localeCompare(a.name.toLowerCase()));
    }
  }

  private _isNoDepartment(department: DepartmentItem): boolean {
    return department.id === '0';
  }

  private _getQueueViewWorkItemType(service: ServiceColumn): string {
    let workItemType = service.workItemType;
    if (workItemType === WorkItemTypes.FACEBOOK || workItemType === WorkItemTypes.ASYNC_MESSAGE) {
      workItemType = WorkItemTypes.SMS;
    }
    return workItemType;
  }

  private _departmentDataUpdated(departmentLoadsModel: DepartmentLoadsModel): void {
    this.departmentList.forEach(department => {
      const departmentLoad = departmentLoadsModel.departmentLoads[department.id];
      if (departmentLoad) {
        department.statusCounts = departmentLoad.statusCounts;
        [department.agentsCount, department.signedInAgentsCount] = this._departmentListTabService.getAgentCounts(departmentLoad.statusCounts);
        department.queueCounts = departmentLoad.queueCounts;
        department.assignedCounts = departmentLoad.assignedCounts;
        department.longestWaitingTimes = departmentLoad.longestWaitingTimes;
      }
    });
    this._maxLongestWaitingTimes = this._getMaxLongestWaitingTimesByServices();
    const allServices = [...this.serviceColumns, ...this.omniIncludedServices];
    this.summary = this._departmentListTabService.getSummaryValues(departmentLoadsModel, allServices);
    this._updateList();
  }

  private _getMaxLongestWaitingTimesByServices(): MaxLongestWaitingTimes {
    return this.departmentList.reduce((longestWaitingTimes, department) => {
      const departmentLongestWaitingTimes = department.longestWaitingTimes;
      this.serviceColumns.forEach(service => {
        if (departmentLongestWaitingTimes && departmentLongestWaitingTimes[service.serviceType]) {
          if (!longestWaitingTimes[service.serviceType] || longestWaitingTimes[service.serviceType].timestamp > departmentLongestWaitingTimes[service.serviceType]) {
            longestWaitingTimes[service.serviceType] = { id: department.id, name: department.name, timestamp: departmentLongestWaitingTimes[service.serviceType]};
          }
        }
      });
      return longestWaitingTimes;
    }, {});
  }

  private _navigateToQueueTab(queueCount: number, service: ServiceColumn, departmentIds: string[]): void {
    if (this.isClickableCount(queueCount)) {
      let workItemType = this._getQueueViewWorkItemType(service);

      this.navigateToOtherTab.emit({ tab: 'QUEUE', workItemType, filterDepartmentIds: departmentIds });
    }
  }

  private _navigateToAssignedTab(count: AssignedCount, service: ServiceColumn, departmentIds: string[]): void {
    if (this.isClickableCount(count && count.assigned)) {
      let workItemType = this._getQueueViewWorkItemType(service);
      this.navigateToOtherTab.emit({ tab: 'ASSIGNED', workItemType, filterDepartmentIds: departmentIds });
    }
  }
}
